import React from 'react'
import styled, { css } from 'styled-components'
import PropTypes from 'prop-types'
import { padding, paddingSizes, borderRadius, borderRadiusSizes, colors, colorConstants, typo, stateThemes, stateColors, formLabelScaleRatio } from 'theme/default'
import Flex from 'components/Flex'
import { H4, SmallText } from 'components/Typo'
import InputState, { inputStates } from './InputState'

const getInputBorderColor = ({ withError, state, focus }) => {
  if (withError) return colorConstants.red
  if (state) return stateColors.borderColors[state][stateThemes.dark]
  if (focus) return colors.text
  return colors.separator
}

const getAddOnBackgroundColor = ({ state }) => {
  if (state) return stateColors.backgroundColors[state][stateThemes.light]
  return colors.background
}

export const inputBorderStyle = css`
  border-radius: ${borderRadius[borderRadiusSizes.small]};
  border: 1px ${getInputBorderColor} solid;

  &:focus {
    border: 1px ${props => props.withError ? colorConstants.red : colors.text} solid;
    outline: none;
  }
`

export const inputStyle = css`
  appearance: none;
  padding: ${padding[paddingSizes.small]};
  padding-top: calc(1em + ${padding[paddingSizes.small]});
  width: 100%;
  box-sizing: border-box;
  color: ${colors.text};
  font-size: ${typo.p.mobileFontSize};
  outline: none;
`

export const InputElement = styled.input`
  ${inputStyle}
  border: none;
  border-radius: 0;
  background-color: ${props => props.disabled ? colorConstants.lightestGray : 'transparent'};
  ${props => props.textTransform ? `text-transform: ${props.textTransform};` : ''}
`

// Need to set width here cos width: auto cause transition doesn't work,
// the value padding[paddingSizes.small] is for right margin,
// the left offset default value is padding[paddingSizes.small] too.
// The value 100% / formLabelScaleRatio is cos it scaled down formLabelScaleRatio so the width sould grow opposite.
export const LabelElement = styled(H4).attrs(
  () => ({
    as: 'label'
  })
)`
  width: calc(100% - (${padding[paddingSizes.small]} + ${props => props.left}));
  position: absolute;
  left: ${props => props.left};
  top: 50%;
  margin: 0;
  transform: scale(1) translateY(-50%);
  transform-origin: 0 0;
  transition: top 0.2s ease-in-out, width 0.2s ease-in-out, transform 0.2s ease-in-out;
  color: ${colors.disabled};
  pointer-events: none;
  /* stylelint-disable declaration-colon-newline-after */
  ${props => props.focus ? `
    width: calc((100% - (${padding[paddingSizes.small]} + ${props.left})) / ${formLabelScaleRatio});
    top: 0.5em;
    transform: scale(${formLabelScaleRatio}) translateY(0);
  ` : ''}
  /* stylelint-enable */
`
LabelElement.defaultProps = {
  left: padding[paddingSizes.small]
}

const AddOnContainer = styled(Flex).attrs(() => ({
  padding: paddingSizes.small,
  justifyContent: 'center',
  alignItems: 'center'
}))``

const AddOn = ({ children, text, ...props }) => (
  <AddOnContainer {...props}>
    {text
      ? <SmallText normalised>{text}</SmallText>
      : children}
  </AddOnContainer>
)
AddOn.propTypes = {
  text: PropTypes.string
}

const InputContainer = styled(Flex)`
  width: 100%;
  background-color: ${colorConstants.white};
  ${inputBorderStyle}

  & ${AddOnContainer} {
    background-color: ${getAddOnBackgroundColor};
  }
`
InputContainer.propTypes = {
  state: PropTypes.oneOf(Object.keys(inputStates)),
  focus: PropTypes.bool
}

const InputElementContainer = styled(Flex).attrs(() => ({
  flexGrow: 1
}))`
  position: relative;
`

/*
  NOTE: Anatomy of InputComponent
  - InputContainer:
    . Concern with rendering border (based on state/withError/focus props)
    . Set background color for AddOn (based on state/withError/focus props)
  - InputElement:
    . The actual HTML input
  - LabelElement:
    . The actual HTML label
  - InputElementContainer:
    . Position-ed div for absolute positioning of LabelElement
  - AddOn:
    . The name explains itself
*/
const InputComponent = React.forwardRef(({ Container, Element, LabelComponent, label, children, ...props }, ref) => (
  <InputState {...props} inputRef={ref}>
    {({ getInputProps, getLabelProps, value, focus, state }) => {
      const inputElement = (
        <InputElementContainer>
          <Element {...getInputProps()} />
          <LabelComponent {...getLabelProps()}>{label}</LabelComponent>
        </InputElementContainer>
      )

      return (
        <Container focus={focus} state={state}>
          {typeof children === 'function'
            ? children({ inputElement, value, focus, getInputProps, getLabelProps })
            : inputElement}
        </Container>
      )
    }}
  </InputState>
))

InputComponent.AddOn = AddOn

InputComponent.propTypes = {
  ...InputState.propTypes,
  label: PropTypes.string.isRequired,
  Element: PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.string]),
  LabelComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.string])
}

InputComponent.defaultProps = {
  Container: InputContainer,
  Element: InputElement,
  LabelComponent: LabelElement
}

export default InputComponent
